import { useMemo, useState } from "react";
import { CheckCircle2, FlaskConical, Play, Plus, Sigma, Trash2, Wrench, XCircle } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, InlineAlert, Input, NativeSelect, Textarea, saveFailureText } from "@ncp/ui";
import { CodeEditor } from "../components/CodeEditor";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { useDefinition, useDefinitionList, fieldKeyFromLabel } from "../features/generic/api";
import { useConnectorCatalog } from "../features/connectors/api";
import type { RecordTypeBody } from "../features/records/api";
import { LOOKUP_OPS, TOOL_INPUT_TYPES, inputSkeleton, useToolTest, useToolTry, type ToolBody, type ToolInput, type ToolRunResult, type ToolTestRun } from "../features/tools/api";

type Ctx = EditorContext<ToolBody>;
type T = (k: string, o?: Record<string, unknown>) => string;

function lint(b: ToolBody, t: T) {
  const errors: string[] = []; const warnings: string[] = [];
  if (!b.purpose.trim()) errors.push(t("admin:tools.lint.purpose"));
  if (b.kind === "formula" && (b.expression === null || b.expression === undefined)) errors.push(t("admin:tools.lint.expression"));
  if (b.kind === "lookup" && !b.lookup?.recordType) errors.push(t("admin:tools.lint.recordType"));
  if (b.kind === "connector" && !b.connectorOperationKey) errors.push(t("admin:tools.lint.operation"));
  if (b.inputs.some((i) => !i.key.trim())) errors.push(t("admin:tools.lint.inputKey"));
  if (b.kind !== "connector" && b.testCases.length === 0) warnings.push(t("admin:tools.lint.noTests"));
  return { errors, warnings };
}

const fmt = (v: unknown) => (v === undefined || v === null ? "—" : typeof v === "string" ? v : JSON.stringify(v));

/** Tool designer: typed inputs, the formula, lookup or connector call behind them, and test cases that must pass before agents may use it. */
export function ToolDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<ToolBody>
      resource="tools"
      basePath="/tools"
      navLabel={t("admin:nav.tools")}
      icon={Wrench}
      writePermission="platform.tools.write"
      docsAnchor="agent-tools"
      help={<><p>{t("admin:tools.help.what")}</p><p>{t("admin:tools.help.kinds")}</p><p>{t("admin:tools.help.tests")}</p></>}
      tabs={[
        { key: "definition", label: t("admin:tools.tabs.definition"), icon: Sigma },
        { key: "tests", label: t("admin:tools.tabs.tests"), icon: FlaskConical },
      ]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:tools.summary", { kind: t(`admin:tools.kinds.${b.kind}`), inputs: b.inputs.length, tests: b.testCases.length })}
      badges={(b) => <Badge variant="outline" size="sm">{t(`admin:tools.kinds.${b.kind}`)}</Badge>}
      render={(tab, ctx) => tab === "tests" ? <TestsTab ctx={ctx} /> : <DefinitionTab ctx={ctx} />}
    />
  );
}

function DefinitionTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly, definition } = ctx;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>{t("admin:tools.purpose.title")}</CardTitle><CardDescription>{t("admin:tools.purpose.intro", { key: `tool.${definition.key}` })}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:tools.fields.purpose")} className="sm:col-span-2">
            <Textarea data-testid="tool-purpose" rows={2} value={body.purpose} disabled={readOnly} onChange={(e) => patch({ purpose: e.target.value })} />
          </Field>
          <Field label={t("admin:tools.fields.output")} hint={t("admin:tools.fields.outputHint")} className="sm:col-span-2">
            <Input data-testid="tool-output" value={body.outputDescription ?? ""} disabled={readOnly} onChange={(e) => patch({ outputDescription: e.target.value || null })} />
          </Field>
        </CardContent>
      </Card>
      <InputsCard ctx={ctx} />
      {body.kind === "formula" ? <FormulaCard ctx={ctx} /> : body.kind === "lookup" ? <LookupCard ctx={ctx} /> : <ConnectorCard ctx={ctx} />}
    </div>
  );
}

function InputsCard({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const set = (i: number, p: Partial<ToolInput>) => patch((b) => ({ ...b, inputs: b.inputs.map((x, j) => (j === i ? { ...x, ...p } : x)) }));
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <div><CardTitle>{t("admin:tools.inputs.title")}</CardTitle><CardDescription>{t("admin:tools.inputs.intro")}</CardDescription></div>
        {!readOnly ? <Button data-testid="add-input" size="sm" variant="outline" onClick={() => patch((b) => ({ ...b, inputs: [...b.inputs, { key: `input${b.inputs.length + 1}`, label: "", type: "number", required: true }] }))}><Plus /> {t("admin:tools.inputs.add")}</Button> : null}
      </CardHeader>
      <CardContent className="space-y-2">
        {body.inputs.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:tools.inputs.none")}</p> : null}
        {body.inputs.map((input, i) => (
          <div key={i} className="grid items-end gap-2 rounded-xl border border-sand-200 p-3 md:grid-cols-[1.2fr_1fr_0.8fr_auto_auto]">
            <Field label={t("admin:tools.inputs.label")}>
              <Input data-testid={`input-label-${i}`} value={input.label ?? ""} disabled={readOnly}
                onChange={(e) => set(i, { label: e.target.value, ...(input.key.startsWith("input") || !input.key ? { key: fieldKeyFromLabel(e.target.value) || input.key } : {}) })} />
            </Field>
            <Field label={t("admin:tools.inputs.key")}><Input data-testid={`input-key-${i}`} value={input.key} dir="ltr" className="font-mono" disabled={readOnly} onChange={(e) => set(i, { key: e.target.value.replace(/[^A-Za-z0-9_]/g, "") })} /></Field>
            <Field label={t("admin:tools.inputs.type")}>
              <NativeSelect data-testid={`input-type-${i}`} value={input.type} disabled={readOnly} onChange={(e) => set(i, { type: e.target.value as ToolInput["type"] })}>
                {TOOL_INPUT_TYPES.map((ty) => <option key={ty} value={ty}>{t(`admin:tools.inputTypes.${ty}`)}</option>)}
              </NativeSelect>
            </Field>
            <label className="flex h-9 items-center gap-2 text-sm"><Checkbox checked={input.required} disabled={readOnly} onCheckedChange={(v) => set(i, { required: v === true })} /> {t("admin:tools.inputs.required")}</label>
            {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:tools.inputs.remove")} onClick={() => patch((b) => ({ ...b, inputs: b.inputs.filter((_, j) => j !== i) }))}><Trash2 /></Button> : <span />}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FormulaCard({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const [text, setText] = useState(() => JSON.stringify(body.expression, null, 2));
  const [invalid, setInvalid] = useState(false);
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:tools.formula.title")}</CardTitle><CardDescription>{t("admin:tools.formula.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-2">
        <CodeEditor value={text} readOnly={readOnly} height="220px" onChange={(v) => { setText(v); try { patch({ expression: JSON.parse(v) }); setInvalid(false); } catch { setInvalid(true); } }} />
        {invalid ? <InlineAlert tone="warning">{t("admin:tools.formula.invalid")}</InlineAlert> : null}
        <p className="text-xs text-muted-foreground">{t("admin:tools.formula.help", { vars: body.inputs.map((i) => i.key).join(", ") || "—" })}</p>
      </CardContent>
    </Card>
  );
}

function LookupCard({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const lookup = body.lookup ?? { recordType: "", match: [], returns: [], limit: 5 };
  const types = useDefinitionList("record-types");
  const type = useDefinition<RecordTypeBody>("record-types", lookup.recordType || undefined);
  const fields = type.data?.fields ?? [];
  const setLookup = (p: Partial<typeof lookup>) => patch({ lookup: { ...lookup, ...p } });
  const match = lookup.match ?? [];
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:tools.lookup.title")}</CardTitle><CardDescription>{t("admin:tools.lookup.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
          <Field label={t("admin:tools.lookup.recordType")}>
            <NativeSelect data-testid="lookup-type" value={lookup.recordType ?? ""} disabled={readOnly} onChange={(e) => setLookup({ recordType: e.target.value, match: [], returns: [] })}>
              <option value="">—</option>
              {types.data?.map((r) => <option key={r.key} value={r.key}>{locale === "ar" && r.nameAr ? r.nameAr : r.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:tools.lookup.limit")}><Input type="number" min={1} max={25} value={lookup.limit ?? 5} disabled={readOnly} onChange={(e) => setLookup({ limit: Number(e.target.value) || 5 })} /></Field>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-sm font-medium">{t("admin:tools.lookup.match")}</span>
            {!readOnly ? <Button size="sm" variant="outline" disabled={!fields.length || !body.inputs.length} onClick={() => setLookup({ match: [...match, { field: fields[0]?.key ?? "", input: body.inputs[0]?.key ?? "", op: "eq" }] })}><Plus /> {t("admin:tools.lookup.addMatch")}</Button> : null}
          </div>
          {match.map((m, i) => (
            <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_0.8fr_1fr_auto]">
              <Field label={t("admin:tools.lookup.field")}>
                <NativeSelect value={m.field} disabled={readOnly} onChange={(e) => setLookup({ match: match.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })}>
                  {fields.map((f) => <option key={f.key} value={f.key}>{locale === "ar" && f.labelAr ? f.labelAr : f.label}</option>)}
                </NativeSelect>
              </Field>
              <Field label={t("admin:tools.lookup.op")}>
                <NativeSelect value={m.op ?? "eq"} disabled={readOnly} onChange={(e) => setLookup({ match: match.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)) })}>
                  {LOOKUP_OPS.map((o) => <option key={o} value={o}>{t(`admin:tools.lookupOps.${o}`)}</option>)}
                </NativeSelect>
              </Field>
              <Field label={t("admin:tools.lookup.input")}>
                <NativeSelect value={m.input} disabled={readOnly} onChange={(e) => setLookup({ match: match.map((x, j) => (j === i ? { ...x, input: e.target.value } : x)) })}>
                  {body.inputs.map((inp) => <option key={inp.key} value={inp.key}>{inp.label || inp.key}</option>)}
                </NativeSelect>
              </Field>
              {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:tools.inputs.remove")} onClick={() => setLookup({ match: match.filter((_, j) => j !== i) })}><Trash2 /></Button> : <span />}
            </div>
          ))}
        </div>
        {fields.length ? (
          <Field label={t("admin:tools.lookup.returns")} hint={t("admin:tools.lookup.returnsHint")}>
            <div className="flex flex-wrap gap-3">
              {fields.map((f) => {
                const on = (lookup.returns ?? []).includes(f.key);
                return (
                  <label key={f.key} className="flex items-center gap-1.5 text-sm">
                    <Checkbox checked={on} disabled={readOnly} onCheckedChange={(v) => setLookup({ returns: v === true ? [...(lookup.returns ?? []), f.key] : (lookup.returns ?? []).filter((k) => k !== f.key) })} />
                    {locale === "ar" && f.labelAr ? f.labelAr : f.label}
                  </label>
                );
              })}
            </div>
          </Field>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ConnectorCard({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const catalog = useConnectorCatalog();
  const operations = useMemo(() => (catalog.data ?? []).flatMap((c) => c.operations.filter((o) => o.readOnly).map((o) => ({ connector: c, op: o }))), [catalog.data]);
  const selected = operations.find((o) => o.op.toolKey === body.connectorOperationKey);
  const pick = (key: string) => {
    const hit = operations.find((o) => o.op.toolKey === key);
    patch((b) => ({
      ...b,
      connectorOperationKey: key || null,
      inputs: hit && b.inputs.length === 0 ? hit.op.parameters.map((p) => ({ key: p.name.replace(/[^A-Za-z0-9_]/g, "_"), label: p.label ?? p.name, type: "text", required: !!p.required && !p.default, description: p.description ?? null })) : b.inputs,
    }));
  };
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:tools.connector.title")}</CardTitle><CardDescription>{t("admin:tools.connector.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <Field label={t("admin:tools.connector.operation")}>
          <NativeSelect data-testid="tool-operation" value={body.connectorOperationKey ?? ""} disabled={readOnly} onChange={(e) => pick(e.target.value)}>
            <option value="">—</option>
            {operations.map(({ connector, op }) => <option key={op.toolKey} value={op.toolKey}>{(locale === "ar" && connector.nameAr ? connector.nameAr : connector.name)} – {locale === "ar" && op.nameAr ? op.nameAr : op.name}</option>)}
          </NativeSelect>
        </Field>
        {selected ? <p className="text-xs text-muted-foreground">{selected.op.description ?? ""} <code className="font-mono">{selected.op.method}</code></p> : null}
        <p className="text-xs text-muted-foreground">{t("admin:tools.connector.readOnly")}</p>
      </CardContent>
    </Card>
  );
}

function TestsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly, definition } = ctx;
  const test = useToolTest();
  const tryIt = useToolTry();
  const [run, setRun] = useState<ToolTestRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState(() => JSON.stringify(inputSkeleton(body.inputs), null, 2));
  const [probeResult, setProbeResult] = useState<ToolRunResult | null>(null);
  const execute = async () => {
    setError(null);
    try { setRun(await test.mutateAsync({ body, key: definition.key })); } catch (e) { setError(saveFailureText(e) || t("admin:common.error")); setRun(null); }
  };
  const tryNow = async () => {
    setError(null); setProbeResult(null);
    // The input is read first, so a typing mistake is not reported as a failed request.
    let input: Record<string, unknown>;
    try { input = JSON.parse(probe) as Record<string, unknown>; } catch (e) { setError((e as Error).message); return; }
    try { setProbeResult((await tryIt.mutateAsync({ body, key: definition.key, input })).result); } catch (e) { setError(saveFailureText(e) || t("admin:common.error")); }
  };
  const setCase = (i: number, p: Partial<ToolBody["testCases"][number]>) => patch((b) => ({ ...b, testCases: b.testCases.map((c, j) => (j === i ? { ...c, ...p } : c)) }));
  return (
    <div className="space-y-4">
      {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
      <Card>
        <CardHeader><CardTitle>{t("admin:tools.try.title")}</CardTitle><CardDescription>{t("admin:tools.try.intro")}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 lg:grid-cols-2">
          <Field label={t("admin:tools.tests.input")}>
            <Textarea data-testid="tool-try-input" rows={6} className="font-mono text-xs" dir="ltr" value={probe} onChange={(e) => setProbe(e.target.value)} />
          </Field>
          <div className="space-y-2">
            <Button data-testid="tool-try" variant="gold" size="sm" loading={tryIt.isPending} onClick={() => void tryNow()}><Play /> {t("admin:tools.try.run")}</Button>
            {probeResult ? (
              <div data-testid="tool-try-result">
                <InlineAlert tone={probeResult.succeeded ? "success" : "danger"} title={probeResult.succeeded ? t("admin:tools.try.ok") : probeResult.error ?? t("admin:tools.try.failed")}>
                  {probeResult.succeeded ? <pre className="whitespace-pre-wrap font-mono text-xs" dir="ltr">{JSON.stringify(probeResult.output, null, 2)}</pre> : null}
                </InlineAlert>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <div><CardTitle>{t("admin:tools.tests.title")}</CardTitle><CardDescription>{t("admin:tools.tests.intro")}</CardDescription></div>
          <div className="flex gap-2">
            {!readOnly ? <Button data-testid="add-test" size="sm" variant="outline" onClick={() => patch((b) => ({ ...b, testCases: [...b.testCases, { name: t("admin:tools.tests.caseName", { n: b.testCases.length + 1 }), input: inputSkeleton(b.inputs), expected: 0 }] }))}><Plus /> {t("admin:tools.tests.add")}</Button> : null}
            <Button data-testid="run-tests" size="sm" variant="gold" loading={test.isPending} disabled={body.testCases.length === 0} onClick={() => void execute()}><Play /> {t("admin:tools.tests.run")}</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {run ? (
            <div data-testid="test-summary">
              <InlineAlert tone={run.passed === run.total ? "success" : "danger"} title={t("admin:tools.tests.summary", { passed: run.passed, total: run.total })}>
                {run.passed === run.total ? t("admin:tools.tests.allPass") : t("admin:tools.tests.someFail")}
              </InlineAlert>
            </div>
          ) : null}
          {body.testCases.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:tools.tests.none")}</p> : null}
          {body.testCases.map((c, i) => {
            const result = run?.results[i];
            return (
              <div key={i} className="grid gap-2 rounded-xl border border-sand-200 p-3 lg:grid-cols-[1fr_1.5fr_1.5fr_auto]">
                <div className="space-y-2">
                  <Input value={c.name} disabled={readOnly} aria-label={t("admin:tools.tests.name")} onChange={(e) => setCase(i, { name: e.target.value })} />
                  {result ? (
                    <div data-testid={`test-result-${i}`} className="flex items-center gap-1.5 text-sm">
                      {result.passed ? <CheckCircle2 className="size-4 text-emerald-600" /> : <XCircle className="size-4 text-red-600" />}
                      {result.passed ? t("admin:tools.tests.passed") : result.error ? result.error : t("admin:tools.tests.got", { value: fmt(result.actual) })}
                    </div>
                  ) : null}
                </div>
                <Field label={t("admin:tools.tests.input")}>
                  <Textarea data-testid={`test-input-${i}`} rows={4} className="font-mono text-xs" dir="ltr" disabled={readOnly} defaultValue={JSON.stringify(c.input, null, 2)}
                    onBlur={(e) => { try { setCase(i, { input: JSON.parse(e.target.value) as Record<string, unknown> }); } catch { /* keep the last valid input */ } }} />
                </Field>
                <Field label={t("admin:tools.tests.expected")} hint={t("admin:tools.tests.expectedHint")}>
                  <Textarea data-testid={`test-expected-${i}`} rows={4} className="font-mono text-xs" dir="ltr" disabled={readOnly} defaultValue={JSON.stringify(c.expected, null, 2)}
                    onBlur={(e) => { try { setCase(i, { expected: JSON.parse(e.target.value) }); } catch { setCase(i, { expected: e.target.value }); } }} />
                </Field>
                {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:tools.inputs.remove")} onClick={() => patch((b) => ({ ...b, testCases: b.testCases.filter((_, j) => j !== i) }))}><Trash2 /></Button> : null}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
